import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Home,
  Calculator,
  FileText,
  Map,
  Users,
  Building2,
  Settings,
  CircleUser,
  Download,
  BarChart3,
  PoundSterling,
  Package,
  ExternalLink,
  FileBarChart
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { getReservationStats } from '../lib/api';

interface NavigationPermissions {
  browse?: boolean;
  campaigns?: boolean;
  longTermListings?: boolean;
  reservations?: boolean;
  export?: boolean;
}

interface AppSidebarProps {
  email: string | null;
  isAdmin: boolean;
  isBrandUser: boolean;
  navigationPermissions?: NavigationPermissions | null;
  onLogout: () => void;
  onNavigate?: () => void; // For closing mobile drawer
  minimized?: boolean;
}

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  adminOnly?: boolean;
  brandUserOnly?: boolean;
  permissionKey?: keyof NavigationPermissions;
}

export default function AppSidebar({
  email,
  isAdmin,
  isBrandUser,
  navigationPermissions,
  onLogout,
  onNavigate,
  minimized = false
}: AppSidebarProps) {
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState<number>(0);

  useEffect(() => {
    if (!isAdmin) {
      setPendingCount(0);
      return;
    }

    // Fetch pending count for admin users
    const fetchPendingCount = () => {
      getReservationStats()
        .then(stats => {
          if (stats && typeof stats.pending === 'number') {
            setPendingCount(stats.pending);
          }
        })
        .catch(err => {
          // Silently fail - don't show errors for this background update
          setPendingCount(0);
        });
    };
    
    // Fetch immediately
    fetchPendingCount();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    return () => clearInterval(interval);
  }, [isAdmin]);

  const navItems: NavItem[] = [
    { to: '/', icon: Home, label: 'Browse', permissionKey: 'browse' },
    { to: '/calculator', icon: Calculator, label: 'Campaigns', permissionKey: 'campaigns' },
    { to: '/new-location-tender', icon: FileText, label: 'Long\u2011Term Listings', brandUserOnly: true, permissionKey: 'longTermListings' },
    { to: '/reservations', icon: FileText, label: 'My Reservations', brandUserOnly: true, permissionKey: 'reservations' },
    { to: '/export', icon: Download, label: 'Machine List Export', permissionKey: 'export' },
    { to: '/admin/map', icon: Map, label: 'Machine Editor', adminOnly: true },
    { to: '/admin/users', icon: Users, label: 'Users', adminOnly: true },
    { to: '/admin/brands', icon: Building2, label: 'Brands', adminOnly: true },
    { to: '/admin/reports', icon: BarChart3, label: 'Reports', adminOnly: true },
    { to: '/admin/custom-reports', icon: FileBarChart, label: 'Custom Reports', adminOnly: true },
    { to: '/admin/reservations', icon: Package, label: 'Pending Requests', adminOnly: true },
    { to: '/admin/location-tenders', icon: ExternalLink, label: 'Public Links', adminOnly: true },
    { to: '/admin/field-options', icon: Settings, label: 'Settings', adminOnly: true },
    { to: '/admin/retail-media-pricing', icon: PoundSterling, label: 'Retail Media Pricing', adminOnly: true },
  ];

  const filteredNavItems = navItems.filter(item => {
    // Admin-only items are always shown to admins
    if (item.adminOnly && !isAdmin) return false;
    
    // Brand user only items
    if (item.brandUserOnly && (!isBrandUser || isAdmin)) return false;
    
    // Check navigation permissions for brand users (admins bypass permissions)
    if (!isAdmin && item.permissionKey && navigationPermissions) {
      const hasPermission = navigationPermissions[item.permissionKey as keyof NavigationPermissions];
      // If permission is explicitly false, hide the item
      // If permission is undefined/null, default to showing (backward compatibility)
      if (hasPermission === false) return false;
    }
    
    return true;
  });

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <div className="flex flex-col h-full bg-blue-600 rounded-r-xl">
      {/* Logo/Brand - Hidden when minimized */}
      {!minimized && (
        <div className="p-4">
          <Link to="/" onClick={handleNavClick} className="flex flex-col items-center gap-2">
            <img 
              src="/images/bb_primary logo (outlined).svg" 
              alt="Bright Blue Logo" 
              className="h-10 w-auto"
            />
            <span className="font-semibold text-sm text-white">Bright.Blue Connect</span>
          </Link>
        </div>
      )}

      {/* Navigation Links */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {filteredNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 rounded-md transition-colors relative",
                minimized ? "justify-center px-2 py-2 mx-1" : "px-4 py-2 mx-2",
                active
                  ? "bg-blue-700 text-white hover:text-white"
                  : "text-white hover:bg-blue-700/50 hover:text-white"
              )}
              title={minimized ? item.label : undefined}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!minimized && (
                <span className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="whitespace-nowrap">{item.label}</span>
                  {item.to === '/admin/reservations' && pendingCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center flex-shrink-0">
                      {pendingCount > 99 ? '99+' : pendingCount}
                    </span>
                  )}
                </span>
              )}
              {minimized && item.to === '/admin/reservations' && pendingCount > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Profile Section at Bottom */}
      {email && !minimized && (
        <div className="mt-auto p-4">
          <div className="flex items-center gap-3 mb-3">
            <CircleUser className="w-5 h-5 text-white/80 flex-shrink-0" />
            <span className="text-sm text-white/80 truncate">{email}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="w-full justify-start text-white hover:bg-blue-700 hover:text-white"
          >
            Logout
          </Button>
        </div>
      )}
      {email && minimized && (
        <div className="mt-auto p-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onLogout}
            className="w-full text-white hover:bg-blue-700 hover:text-white"
            title="Logout"
          >
            <CircleUser className="w-5 h-5" />
          </Button>
        </div>
      )}
    </div>
  );
}

